/**
 * Equivalent of the jQuery.ready() function.
 * Executes the callback when the document is fully loaded.
 *
 * @param {Function} callback - The function to be executed when the document is ready.
 */
export function onDocumentReady(callback) {
	if (document.readyState === "complete" || (document.readyState !== "loading" && !document.documentElement.doScroll)) {
		// Dokument už je načtený, takže musíme zavolat callback my (událost DOMContentLoaded
		// už nebude prohlížečem emitována).
		callback();
	}
	else {
		// Dokument ještě není načtený, počkáme na něj přes událost.
		document.addEventListener("DOMContentLoaded", callback);
	}
}


/**
 * Handler for code that is added to the page via snippets.
 * Executes the callback when the document is ready or when a snippet is ready.
 *
 * @param {Function} callback - The function to be executed when the content is ready.
 */
export function onContentReady(callback) {
	onDocumentReady(function() {
		callback(document)
	});

	document.addEventListener("snippetready", function(event) {
		callback(event.detail.parent);
	});
}


/**
 * Simplified way of creating custom events. Events can have their own parameters
 * (available in the handler via event.detail.*), can "bubble" upwards, or be "cancelable"
 * (both set to false by default).
 *
 * This function is crucial as we use custom events extensively on the web.
 *
 * The function needs to be called using "apply" or "bind" in the form of
 * triggerEvent.apply(element, eventName, params, bubbles, cancellable).
 * It expects to receive the element on which to trigger the event in "this".
 *
 * @param {string} eventName - The name of the event to be triggered.
 * @param {Object} params - The parameters to be passed with the event.
 * @param {boolean} [bubbles=true] - Whether the event should bubble up.
 * @param {boolean} [cancellable=true] - Whether the event should be cancelable.
 */
export function triggerEvent(eventName, params, bubbles, cancellable) {
	// Výchozí hodnoty pro probublávání a zrušitelnost.
	bubbles = bubbles || true;
	cancellable = cancellable || true;

	// Vytvoření vlastní události se zvoleným názvem.
	var customEvent = new window.CustomEvent(eventName, { bubbles: bubbles, cancellable: cancellable, detail: params })

	// Vyvolání události na DOM elementu, který nám byl předán ve formě "this" proměnné.
	this.dispatchEvent(customEvent);
}


/**
 * Is the DOM element visible?
 * The DOM element is passed via "apply" as the "this" pointer.
 * Function taken from the jQuery library.
 */
export function isVisible() {
	return !!(this.offsetWidth || this.offsetHeight || this.getClientRects().length) && this.style.visibility != "hidden";
}


/**
 * Return direct descendants of the given element that match the given selector.
 * Unfortunately, IE does not implement the querySelectorAll(":scope > ...") option,
 * so we need to find another way.
 * The DOM element is passed via "apply" as the "this" pointer.
 *
 * @param {string} selector - The CSS selector to match the descendants against.
 * @returns {Array} - An array of matching direct descendant elements.
 */
export function querySelectorChildren(selector) {
	return Array.prototype.filter.call(this.children, function(child) {
		return child.matches(selector);
	});
};


/**
 * Simplifies event capturing that is bound to a specific element (selector).
 *
 * @param {Element} element - The DOM element to bind the event to.
 * @param {string} event - The event type to listen for.
 * @param {string} selector - The CSS selector to match the descendants against.
 * @param {Function} handler - The event handler function.
 */
export function addEventSelectorListener(eventNames, selector, callback) {
	var eventNames = eventNames.split(" ");
	for (var i = 0; i < eventNames.length; ++i) {
		this.addEventListener(eventNames[i], function(event) {
			// Dostaneme-li jako event.target objekt document, ten nemá metodu closest, a proto musíme její přítomnost testovat.
			var target = event.target.closest ? event.target.closest(selector) : null;
			if (!target) { return; }
			callback(event, target);
		});
	}
}


/**
 * A relaxed version of addEventSelectorListener, where "closest" is not used,
 * but the selector must match directly on the element that triggered the event.
 * This is particularly useful for events like "change", where the event source
 * is directly an input element and checking "closest" would be unnecessary
 * (it wouldn't be fundamentally wrong, but it would reduce script performance).
 *
 * @param {Element} element - The DOM element to bind the event to.
 * @param {string} event - The event type to listen for.
 * @param {string} selector - The CSS selector to match the event target against.
 * @param {Function} handler - The event handler function.
 */
export function addEventMatchesListener(eventName, selector, callback) {
	this.addEventListener(eventName, function(event) {
		if (event.target.matches(selector)) {
			callback(event, event.target);
		}
	});
}


/**
 * If it is a link that does not point anywhere or a button that is not "submit",
 * it suppresses the action. Useful for suppressing events on links that lead nowhere
 * (href is #), while preserving functionality for elements like radio buttons.
 */
export function preventLinkDefault(event, target) {
	if (target.getAttribute("href") == "#"
		|| (target.matches("button") && !target.matches("button[type='submit'], button[type='reset']"))
		|| (target.matches("input") && !target.matches("input[type='checkbox'], input[type='radio']"))) {
		event.preventDefault();
	}
}